import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Gauge, Clock, ShieldCheck, ListOrdered, RefreshCw } from "lucide-react";
import { Reveal, SectionTitle, Overline } from "@/components/Reveal";
import { genSignal } from "@/data/mock";

export const SignalGenerator = () => {
  const [state, setState] = useState("idle"); // idle | loading | done
  const [result, setResult] = useState(null);

  const generate = () => {
    setState("loading");
    setResult(null);
    setTimeout(() => {
      setResult(genSignal());
      setState("done");
    }, 3000);
  };

  return (
    <section id="sinais" className="relative py-20 md:py-28" data-testid="signal-section">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] radial-gold blur-3xl opacity-60 pointer-events-none" />
      <div className="max-w-4xl mx-auto px-5 md:px-8 relative">
        <Reveal className="text-center mb-10">
          <Overline>Sinais em Tempo Real</Overline>
          <SectionTitle className="mt-3">
            Gerador de <span className="text-gold-gradient">Sinais</span>
          </SectionTitle>
          <p className="mt-4 text-white/55 max-w-xl mx-auto font-body">
            Gere um sinal agora. Os valores não representam garantia de
            resultados.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass-gold p-8 md:p-12 flex flex-col items-center">
            {/* Radar / mango */}
            <div className="relative w-40 h-40 flex items-center justify-center mb-8">
              <div className="absolute inset-0 radial-gold blur-xl" />
              {state === "loading" && (
                <>
                  <div
                    className="absolute inset-0 rounded-full border border-[#F5C542]/20"
                    style={{
                      background:
                        "conic-gradient(from 0deg, rgba(245, 197, 66,0) 0deg, rgba(245, 197, 66,0.35) 60deg, rgba(245, 197, 66,0) 90deg)",
                      animation: "radarSweep 1.2s linear infinite",
                    }}
                  />
                  <div className="absolute inset-4 rounded-full border border-[#F5C542]/15" />
                  <div className="absolute inset-10 rounded-full border border-[#F5C542]/10" />
                </>
              )}
              <motion.img
                src="/manga-symbol.png"
                alt="Grupo Manga"
                className="relative w-24 h-24 object-contain gold-glow"
                animate={
                  state === "loading"
                    ? { scale: [1, 1.08, 1] }
                    : { scale: 1 }
                }
                transition={{ duration: 1, repeat: state === "loading" ? Infinity : 0 }}
              />
            </div>

            <AnimatePresence mode="wait">
              {state !== "done" && (
                <motion.button
                  key="btn"
                  exit={{ opacity: 0, y: -10 }}
                  onClick={generate}
                  disabled={state === "loading"}
                  className="btn-gold font-display font-bold text-lg px-10 py-4 rounded-full flex items-center gap-3 hover:scale-105 transition-transform duration-300 disabled:opacity-70 disabled:hover:scale-100"
                  data-testid="generate-signal-button"
                >
                  <Target className="w-5 h-5" />
                  {state === "loading" ? "Analisando sinais..." : "Gerar Sinais"}
                </motion.button>
              )}
            </AnimatePresence>

            {state === "loading" && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 text-sm text-white/50 tracking-wider animate-pulse"
              >
                Processando sinais...
              </motion.p>
            )}

            <AnimatePresence>
              {state === "done" && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 20, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full"
                  data-testid="signal-result"
                >
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00FF41]/10 border border-[#00FF41]/30 text-[#00FF41] text-xs font-bold uppercase tracking-wider">
                      Sinal Gerado
                    </div>
                    <div className="font-display text-3xl font-bold text-white mt-4">
                      {result.game}
                    </div>
                    <div className="text-[#F5C542] text-sm mt-1">
                      {result.provider}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <SigStat icon={Gauge} label="Sinal Atual" value={`${result.rtp.toFixed(2)}%`} color={result.level.color} />
                    <SigStat icon={Clock} label="Melhor Horário" value={result.time} />
                    <SigStat icon={ShieldCheck} label="Confiança" value={`${result.confidence}%`} />
                    <SigStat icon={ListOrdered} label="Sequência" value={result.sequence} small />
                  </div>

                  <div className="flex justify-center mt-8">
                    <button
                      onClick={generate}
                      className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white/80 hover:border-[#F5C542]/50 hover:text-white transition-colors duration-300 font-display text-sm"
                      data-testid="regenerate-signal-button"
                    >
                      <RefreshCw className="w-4 h-4" /> Gerar Novamente
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const SigStat = ({ icon: Icon, label, value, color = "#FFFFFF", small }) => (
  <div className="glass-card p-4 text-center">
    <Icon className="w-5 h-5 text-[#F5C542] mx-auto mb-2" />
    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
      {label}
    </div>
    <div
      className={`font-display font-bold ${small ? "text-sm" : "text-lg"}`}
      style={{ color }}
    >
      {value}
    </div>
  </div>
);
